import React from 'react';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, Settings2 } from 'lucide-react';
import { when } from './aiqMeta';

const Stat = ({ label, value, testId }) => (
  <div className="min-w-[88px]">
    <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="font-mono text-base text-zinc-900" data-testid={testId}>{value ?? '—'}</div>
  </div>
);

export const SyncStatusBar = ({ status, refreshing, onRefresh, onSettings }) => {
  const cache = status?.cache || {};
  const ok = status?.enabled && status?.last_sync_at && !status?.last_sync_error && status?.health?.ok !== false;
  return (
    <div className="rounded-lg border bg-white p-4 space-y-3" data-testid="aiq-sync-status">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-sm">
          {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
          <span className="font-medium" data-testid="aiq-connection-state">
            {!status?.configured ? 'Not configured' : !status?.enabled ? 'Switched off' : status?.health?.ok === false ? 'Connection problem' : 'Connected'}
          </span>
          <span className="text-zinc-500">· Last refresh: <span className="font-mono" data-testid="aiq-last-sync">{when(status?.last_sync_at)}</span></span>
          {status?.last_sync_stats?.reference_source && (
            <span className="text-zinc-500">· Tax &amp; currency via {status.last_sync_stats.reference_source === 'rest' ? 'REST API' : 'SOAP'}</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onSettings} data-testid="aiq-open-settings">
            <Settings2 className="w-4 h-4 mr-1.5" /> Connection settings
          </Button>
          <Button size="sm" onClick={onRefresh} disabled={refreshing || !status?.enabled} className="bg-[#005F73] hover:bg-[#0A9396]" data-testid="aiq-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            {refreshing ? 'Refreshing…' : 'Refresh from AccountsIQ'}
          </Button>
        </div>
      </div>
      {status?.last_sync_error && (
        <div className="text-xs rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2" data-testid="aiq-sync-error">
          Last refresh failed: {status.last_sync_error}
        </div>
      )}
      {status?.last_sync_stats?.note && (
        <div className="text-xs rounded border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2">{status.last_sync_stats.note}</div>
      )}
      <div className="flex flex-wrap gap-6">
        <Stat label="Customers" value={cache.customers} testId="aiq-count-customers" />
        <Stat label="Stock items" value={cache.stock_items} testId="aiq-count-stock" />
        <Stat label="Price lists" value={cache.price_lists} testId="aiq-count-pricelists" />
        <Stat label="Tax codes" value={cache.tax_codes} testId="aiq-count-tax" />
        <Stat label="Currencies" value={cache.currencies} testId="aiq-count-currencies" />
        <Stat label="Open issues" value={status?.open_issues} testId="aiq-count-issues" />
      </div>
    </div>
  );
};
